import {useEffect, useRef, useState} from "react";
import {Box, CircularProgress} from "@mui/material";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import type {ResolvedGallery} from "../types";
import "./GalleryPhotos.css"

const PAGE_SIZE = 48;

type Props = {
    gallery: ResolvedGallery;
    currentPhotoFilename?: string;           // if set, open lightbox on this photo
    onOpenPhoto?: (filename: string) => void; // called when user clicks a thumb
    onPhotoChange?: (filename: string) => void; // called when user navigates in lightbox
    onCloseLightbox?: () => void;
};

export default function GalleryPhotos({
                                        gallery,
                                        currentPhotoFilename,
                                        onOpenPhoto,
                                        onPhotoChange,
                                        onCloseLightbox,
                                    }: Props) {
    const [visible, setVisible] = useState(PAGE_SIZE);
    const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
    const [slides, setSlides] = useState<any>([]);

    const sentinelRef = useRef<HTMLDivElement | null>(null);

    const photos = gallery.photos;

    useEffect(() => {
        if (!currentPhotoFilename && lightboxIdx !== null) {
            setLightboxIdx(null);
            return;
        }
        const idx = photos.findIndex((p) => p.filename === currentPhotoFilename);
        if (idx >= 0 && lightboxIdx === null) setLightboxIdx(idx);
    }, [currentPhotoFilename, photos, lightboxIdx]);

    useEffect(() => {
        setSlides(photos.map((p) => ({src: p.sizeUrls[0].url, description: p.title})));
    }, [photos]);


    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) setVisible((v) => v + PAGE_SIZE);
            },
            {rootMargin: "1200px 0px"}
        );
        io.observe(el);
        return () => io.disconnect();
    }, [sentinelRef]);

    if (!gallery) {
        return (
            <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                <CircularProgress/>
            </Box>
        );
    }

    return (
        <>
            {/* Thumbnails */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: 2,
                }}
            >
                {photos.map((p, i) => (
                    <button
                        key={`${p.filename}-${i}`}
                        onClick={() => {
                            setLightboxIdx(i);
                            onOpenPhoto?.(p.filename); // push URL
                        }}
                        aria-label={`Open ${p.title || p.filename}`}
                        className="image-thumbnail"
                    >
                        <img
                            src={p.sizeUrls[2].url}
                            alt={p.title || p.filename}
                            loading="lazy"
                            style={{width: "100%", height: 240, objectFit: "cover", display: "block"}}
                        />
                    </button>
                ))}
            </Box>

            {visible < photos.length && <div ref={sentinelRef} style={{height: 1}}/>}

            <Lightbox
                open={lightboxIdx !== null}
                index={lightboxIdx ?? 0}
                close={() => {
                    setLightboxIdx(null);
                    onCloseLightbox?.(); // pop back to /g/:id
                }}
                slides={slides}
                plugins={[Fullscreen]}
                carousel={{finite: false}}
                controller={{closeOnBackdropClick: true}}
                on={{
                    view: (index: any) => {
                        const i = index.index;
                        if (i !== lightboxIdx) {
                            setLightboxIdx(i);
                            const filename = photos[i]?.filename;
                            if (filename) onPhotoChange?.(filename); // replace URL /g/:id/p/:photo
                        }
                    },
                } as any}
            />
        </>
    );
}
